import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AddressService } from '../../../services/address.service';
import { AddressModel } from '../../../models/address.model';
import { AddressUtils } from '../../../utils/address-utils';

@Component({
  selector: 'delivery-address-picker',
  templateUrl: './delivery-address-picker.component.html',
  styleUrls: ['./delivery-address-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DeliveryAddressPickerComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() addressControlName: string = 'deliveryAddress';
  @Input() establishmentControlName: string = 'deliveryEstablishment';
  @Input() defaultEstablishmentId: string;
  @Input() defaultAddressId: string;

  @Output() addressSelected = new EventEmitter<AddressModel>();
  @Output() establishmentSelected = new EventEmitter<{ _id: string; label: string }>();

  deliveryAddress$: Observable<AddressModel[]>;
  deliveryEstablishments: Array<{ _id: string; label: string }> = [];
  showEstablishmentPicker = false;

  private initialLoadDone = false;
  private subscription = new Subscription();

  constructor(private addressService: AddressService) {}

  ngOnInit() {
    this.loadAddresses('');
  }

  loadAddresses(searchTerm: string) {
    this.deliveryAddress$ = this.addressService
      .findAll(null, 1000, 'deliverySort', 'desc', searchTerm, {
        isActive: true,
        requiredFields: ['*'],
      })
      .pipe(
        map(response => {
          if (response?.data) {
            const addresses = response.data.map(address => {
              const establishments = AddressUtils.extractEstablishments(address);
              const labels = establishments.map(e => e.label);
              (address as any).label = AddressUtils.formatAddressFirst(address, labels);
              (address as any)._establishments = establishments;
              return address;
            });

            if (!this.initialLoadDone) {
              this.initialLoadDone = true;
              const currentValue = this.formGroup.get(this.addressControlName)?.value;
              const targetId = currentValue?._id || this.defaultAddressId;
              if (targetId) {
                const existing = addresses.find(a => a._id === targetId);
                if (existing) {
                  this.formGroup.get(this.addressControlName).setValue(existing, { emitEvent: false });
                  this.handleAddressSelection(existing);
                }
              }
            }

            return addresses;
          }
          return [];
        }),
        shareReplay(1),
      );
  }

  onAutocompleteChange(searchTerm: string) {
    this.loadAddresses(searchTerm);
  }

  onAddressSelected(event: any) {
    const address = event?.option ? event.option.value : event;
    this.handleAddressSelection(address);
  }

  private handleAddressSelection(address: any) {
    if (!address || !address._establishments) {
      this.showEstablishmentPicker = false;
      this.deliveryEstablishments = [];
      this.formGroup.get(this.establishmentControlName)?.setValue(null);
      this.addressSelected.emit(address);
      return;
    }

    const establishments: Array<{ _id: string; label: string }> = address._establishments;

    if (establishments.length === 1) {
      this.showEstablishmentPicker = false;
      this.deliveryEstablishments = [];
      this.formGroup.get(this.establishmentControlName)?.setValue(establishments[0]);
      this.establishmentSelected.emit(establishments[0]);
    } else if (establishments.length > 1) {
      this.showEstablishmentPicker = true;
      this.deliveryEstablishments = establishments;

      const currentValue = this.formGroup.get(this.establishmentControlName)?.value;
      const existingEstablishment = currentValue?._id
        ? establishments.find(e => e._id === currentValue._id)
        : null;

      const defaultEstablishment = existingEstablishment
        || (this.defaultEstablishmentId ? establishments.find(e => e._id === this.defaultEstablishmentId) : null);

      if (defaultEstablishment) {
        this.formGroup.get(this.establishmentControlName)?.setValue(defaultEstablishment);
        this.establishmentSelected.emit(defaultEstablishment);
      } else {
        this.formGroup.get(this.establishmentControlName)?.setValue(null);
      }
    } else {
      this.showEstablishmentPicker = false;
      this.deliveryEstablishments = [];
      this.formGroup.get(this.establishmentControlName)?.setValue(null);
    }

    this.addressSelected.emit(address);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
